import { PostModel } from '@pulsex/models';
import { getLoadMore } from '@pulsex/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { removePageValue } from '@pulsex/utils';
const LOAD_MORE_PAGE_SIZE = 5;
const getPostsCountFromPage = (page) => {
    var _a;
    if ((page === null || page === void 0 ? void 0 : page.widgets) && ((_a = page.widgets[0]) === null || _a === void 0 ? void 0 : _a.posts)) {
        return Array.isArray(page.widgets[0].posts) ? page.widgets[0].posts.length : 0;
    }
    if (Array.isArray(page === null || page === void 0 ? void 0 : page.posts)) {
        return page.posts.length;
    }
    if (Array.isArray(page)) {
        return page.length;
    }
    return 0;
};
export default function useLoadMore(config, url, initialData, queryKeys = [], enabled = true) {
    var _a;
    const query = useInfiniteQuery(['LOADMORE', url, ...queryKeys], ({ pageParam = 1 }) => {
        const NO_PAGE_PARAM_URL = removePageValue(url);
        return getLoadMore(config, `${NO_PAGE_PARAM_URL}${NO_PAGE_PARAM_URL.includes('?') ? '&' : '?'}page=${pageParam}`);
    }, {
        // Widgets whose CMS payload merely *contains* a loadMoreLink but
        // whose `code` doesn't opt into pagination (see `enableLoadMore` at
        // each call site) never read `data` from this query — without this
        // flag every such widget instance still fired its own page-1 fetch
        // on mount. PLATFORM-ALAKHBAR-8C.
        enabled: !!url && enabled,
        initialData: { pageParams: [undefined], pages: [initialData] },
        getNextPageParam: (lastPage, pages) => {
            const postsCount = getPostsCountFromPage(lastPage);
            // If the last page has no posts, or fewer than LOAD_MORE_PAGE_SIZE,
            // we assume there is no next page.
            // This is done because the API doesn’t tell us "there are no more pages" in the first response,
            // and the first page has 2 posts (in my case), our getNextPageParam correctly assumes “there might be more, try page 2”,
            // so hasNextPage is true on first render.
            // To make hasNextPage false immediately, you’d need some signal from the backend
            // (e.g. hasMore, nextPage, totalPages, or rely on a known page size rule like if (posts.length < PAGE_SIZE) stop).
            if (postsCount === 0 || postsCount < LOAD_MORE_PAGE_SIZE) {
                return undefined;
            }
            return pages.length + 1;
        },
        refetchOnWindowFocus: false,
    });
    const postMapper = new PostModel(config);
    const mappedPosts = [];
    if ((query === null || query === void 0 ? void 0 : query.isSuccess) && (query === null || query === void 0 ? void 0 : query.data)) {
        (_a = query === null || query === void 0 ? void 0 : query.data) === null || _a === void 0 ? void 0 : _a.pages.map((responseData) => {
            var _a;
            if (responseData === null || responseData === void 0 ? void 0 : responseData.widgets) {
                responseData === null || responseData === void 0 ? void 0 : responseData.widgets.map((widgetData) => {
                    var _a, _b;
                    if ((_a = widgetData === null || widgetData === void 0 ? void 0 : widgetData.posts) === null || _a === void 0 ? void 0 : _a.length) {
                        const posts = (_b = widgetData === null || widgetData === void 0 ? void 0 : widgetData.posts) === null || _b === void 0 ? void 0 : _b.map((post) => postMapper.mapPost(post));
                        if (posts === null || posts === void 0 ? void 0 : posts.length) {
                            posts === null || posts === void 0 ? void 0 : posts.map((item) => {
                                item && (mappedPosts === null || mappedPosts === void 0 ? void 0 : mappedPosts.push(item));
                            });
                        }
                    }
                });
            }
            else {
                const posts = (_a = responseData === null || responseData === void 0 ? void 0 : responseData.posts) === null || _a === void 0 ? void 0 : _a.map((post) => postMapper.mapPost(post));
                if (posts === null || posts === void 0 ? void 0 : posts.length) {
                    posts === null || posts === void 0 ? void 0 : posts.forEach((item) => {
                        mappedPosts === null || mappedPosts === void 0 ? void 0 : mappedPosts.push(item);
                    });
                }
                else {
                    responseData === null || responseData === void 0 ? void 0 : responseData.forEach((post) => mappedPosts === null || mappedPosts === void 0 ? void 0 : mappedPosts.push(post));
                }
            }
        });
    }
    return {
        isLoading: query.isLoading,
        data: mappedPosts,
        fetchNextData: query.fetchNextPage,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage,
    };
}
